import { AdInfo, AppUser, ClipInfo, EditionMap, FlashItem, PaperInfo, Session, ShareInfo, ViewRow, ViewStats } from './types';

/**
 * Where the API lives. Normally the same site (the jar serves both), so this is empty.
 * While developing with `npm start` (port 3000) the Spring Boot server is on port 8080, so call it directly
 * (the backend allows this with CORS). REACT_APP_API_URL overrides both.
 */
function resolveBase(): string {
  const fromEnv = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  const l = window.location;
  if ((l.protocol === 'http:' || l.protocol === 'https:') && l.port === '3000') return `${l.protocol}//${l.hostname}:8080`;
  return '';
}
const BASE = resolveBase();

const KEY = 'epaper.session';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let session: Session | null = null;
let onUnauthorized: () => void = () => undefined;

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    session = raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    session = null;
  }
  return session;
}

export function setSession(s: Session | null): void {
  session = s;
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: keep in memory only */
  }
}

export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

function authHeader(): Record<string, string> {
  return session ? { Authorization: 'Bearer ' + session.token } : {};
}

async function parse<T>(res: Response, where = ''): Promise<T> {
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const given = (data as { error?: string } | null)?.error;
    // no message from our server = the request did not reach the Royal Journalism backend at all
    const msg =
      given ||
      (res.status === 404 || res.status === 502 || res.status === 503
        ? `The Royal Journalism server was not found at ${where || BASE || window.location.origin} (error ${res.status}). Check that the backend is running and is the latest version.`
        : `Request failed (${res.status})`);
    if (res.status === 401 && session) onUnauthorized();
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + '/api' + path, {
      method,
      headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...authHeader() },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'Cannot reach the server. Check your internet connection.');
  }
  return parse<T>(res, BASE + '/api' + path);
}

/** Sends a file with upload progress (0-100). */
function sendFile<T>(method: string, path: string, form: FormData, onProgress?: (pct: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open(method, BASE + '/api' + path);
    const h = authHeader();
    Object.keys(h).forEach((k) => x.setRequestHeader(k, h[k]));
    x.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    x.onerror = () => reject(new ApiError(0, 'Cannot reach the server. Check your internet connection.'));
    x.onload = () => {
      let data: { error?: string } | null = null;
      try {
        data = JSON.parse(x.responseText);
      } catch {
        data = null;
      }
      if (x.status >= 200 && x.status < 300) resolve(data as unknown as T);
      else {
        if (x.status === 401 && session) onUnauthorized();
        reject(new ApiError(x.status, data?.error || `Upload failed (${x.status}) at ${BASE + '/api' + path}`));
      }
    };
    x.send(form);
  });
}

/** A link to a screen of this site, built from the address the site is opened on (works on localhost, :3000, the cloud ...). */
export function siteLink(path: string): string {
  const l = window.location;
  return `${l.origin}${l.pathname}#${path}`;
}

/** Address of a server page (not a screen of the app), e.g. https://site/share/TOKEN – chat apps read it to show the page picture. */
function rootLink(path: string): string {
  const l = window.location;
  return `${l.origin}${l.pathname.replace(/[^/]*$/, '')}${path.replace(/^\//, '')}`;
}
const withShareUrl = (s: ShareInfo): ShareInfo => ({ ...s, url: rootLink('share/' + s.token) });
const withClipUrl = (c: ClipInfo): ClipInfo => ({ ...c, url: rootLink('clip/' + c.token) });

export const api = {
  loginCode: (code: string) => call<Session>('POST', '/auth/code', { code }),
  recoverAdmin: (username: string, recoveryKey: string, newPassword: string) =>
    call<{ ok: boolean }>('POST', '/auth/admin/recover', { username, recoveryKey, newPassword }),
  loginAdmin: (username: string, password: string) => call<Session>('POST', '/auth/admin', { username, password }),

  latest: () => call<{ today: string; paper: PaperInfo | null }>('GET', '/papers/latest'),
  month: (month: string) => call<PaperInfo[]>('GET', '/papers/dates?month=' + month),
  all: () => call<PaperInfo[]>('GET', '/papers/dates'),
  /** The newspaper of a day, or null when nothing is uploaded for it. */
  paper: (date: string) => call<{ paper: PaperInfo | null }>('GET', '/papers/' + date + '/info').then((r) => r.paper),
  upload: (date: string, file: File, replace: boolean, onProgress?: (p: number) => void) => {
    const f = new FormData();
    f.append('file', file);
    return sendFile<PaperInfo>(replace ? 'PUT' : 'POST', replace ? '/admin/papers/' + date : '/papers/' + date, f, onProgress);
  },
  /** Delete the paper of a day (uploader or admin) so it can be uploaded again. */
  deleteMyPaper: (date: string) => call<{ ok: boolean }>('DELETE', '/papers/' + date),
  deletePaper: (date: string) => call<{ ok: boolean }>('DELETE', '/admin/papers/' + date),

  share: (date: string, page: number, effect: string, sound: string) => call<ShareInfo>('POST', '/shares', { date, page, effect, sound }).then(withShareUrl),
  publicMonth: (token: string, month: string) => call<PaperInfo[]>('GET', `/public/s/${token}/dates?month=${month}`),
  paperViewers: (date: string) => call<{ date: string; paper: number; total: number; papers: number }>('GET', '/paper-viewers/' + date),
  setPaperViewers: (date: string, count: number) => call<{ date: string; paper: number; total: number; papers: number }>('PUT', '/paper-viewers/' + date, { count }),
  setTotalViewers: (date: string, total: number) => call<{ date: string; paper: number; total: number; papers: number }>('PUT', '/paper-viewers/total/' + date, { total }),
  publicAds: (token: string) => call<{ ads: AdInfo[] }>('GET', `/public/s/${token}/promo`).then((r) => r.ads),
  publicDayLink: (token: string, date: string) => call<{ token: string; date: string; page: number }>('POST', `/public/s/${token}/days/${date}/link`),
  publicEditions: (token: string) => call<{ editions: EditionMap }>('GET', `/public/s/${token}/editions`).then((r) => r.editions),
  publicPageLink: (token: string, page: number) => call<ShareInfo>('POST', `/public/s/${token}/pages/${page}/link`).then(withShareUrl),
  publicShare: (token: string) => call<{ date: string; page: number; pageCount: number; effect?: string; sound?: string }>('GET', '/public/s/' + token),

  saveClip: (blob: Blob, date: string, page: number) => {
    const f = new FormData();
    f.append('image', blob, 'clipping.jpg');
    f.append('date', date);
    f.append('page', String(page));
    return sendFile<ClipInfo>('POST', '/crops', f).then(withClipUrl);
  },
  clips: () => call<ClipInfo[]>('GET', '/crops').then((l) => l.map(withClipUrl)),
  deleteClip: (id: number) => call<{ ok: boolean }>('DELETE', '/crops/' + id),
  publicClip: (token: string) => call<ClipInfo>('GET', '/public/c/' + token).then(withClipUrl),

  editions: (date: string) => call<{ editions: EditionMap }>('GET', `/papers/${date}/editions`).then((r) => r.editions),
  setEditions: (date: string, editions: EditionMap) =>
    call<{ editions: EditionMap }>('PUT', `/admin/papers/${date}/editions`, { editions }).then((r) => r.editions),

  ads: (date: string) => call<{ ads: AdInfo[] }>('GET', '/promo/' + date).then((r) => r.ads),
  saveAd: (date: string, slot: number, image: File) => {
    const f = new FormData();
    f.append('image', image);
    f.append('date', date);
    f.append('slot', String(slot));
    return sendFile<AdInfo>('POST', '/promo', f);
  },
  deleteAllAds: (date: string) => call<{ ok: boolean }>('DELETE', `/promo/${date}`),
  deleteAd: (date: string, slot: number) => call<{ ok: boolean }>('DELETE', `/promo/${date}/${slot}`),

  shareViews: (token: string) => call<ViewStats>('GET', '/views/share/' + token),
  clipViews: (token: string) => call<ViewStats>('GET', '/views/clip/' + token),
  viewsOverview: () => call<ViewRow[]>('GET', '/views'),

  publicFlash: () => call<{ items: FlashItem[] }>('GET', '/public/flash').then((r) => r.items),
  flash: () => call<{ items: FlashItem[] }>('GET', '/flash').then((r) => r.items),
  addFlash: (text: string, slot: number) => call<FlashItem>('POST', '/flash', { text, slot: String(slot) }),
  editFlash: (id: number, text: string) => call<FlashItem>('PUT', '/flash/' + id, { text }),
  deleteFlash: (id: number) => call<{ ok: boolean }>('DELETE', '/flash/' + id),
  deleteAllFlash: (slot: number) => call<{ ok: boolean }>('DELETE', '/flash?slot=' + slot),

  users: () => call<AppUser[]>('GET', '/admin/users'),
  createUser: (name: string) => call<AppUser>('POST', '/admin/users', { name }),
  changeMyCode: (currentCode: string, newCode: string) => call<{ ok: boolean }>('POST', '/me/code', { currentCode, newCode }),
  userCode: (id: number) => call<{ code: string }>('GET', `/admin/users/${id}/code`),
  resetUser: (id: number) => call<AppUser>('POST', `/admin/users/${id}/reset`),
  resetByName: (name: string) => call<AppUser>('POST', '/admin/users/reset-by-name', { name }),
  setActive: (id: number, active: boolean) => call<AppUser>('PATCH', `/admin/users/${id}/active`, { active }),
  deleteUser: (id: number) => call<{ ok: boolean }>('DELETE', `/admin/users/${id}`),
  makeRecoveryKey: (password: string) => call<{ key: string }>('POST', '/admin/recovery-key', { password }),
  recoveryKeyExists: () => call<{ exists: boolean }>('GET', '/admin/recovery-key'),
  changePassword: (oldPassword: string, newPassword: string) =>
    call<{ ok: boolean }>('POST', '/admin/password', { oldPassword, newPassword }),
};

/* ---- picture addresses (images cannot send headers, so the token rides in the address) ---- */
export function pageImageUrl(date: string, page: number, w = 1800): string {
  const t = session ? '&t=' + encodeURIComponent(session.token) : '';
  return `${BASE}/api/papers/${date}/pages/${page}/image?w=${w}${t}`;
}

/** The uploaded PDF of a day. The server names the file after the date (2026-10-14.pdf). */
export function paperPdfUrl(date: string): string {
  const t = session ? '?t=' + encodeURIComponent(session.token) : '';
  return `${BASE}/api/papers/${date}/pdf${t}`;
}

export function sharedPdfUrl(token: string): string {
  return `${BASE}/api/public/s/${token}/pdf`;
}

export function sharedPageImageUrl(token: string, page: number, w = 1800): string {
  return `${BASE}/api/public/s/${token}/pages/${page}/image?w=${w}`;
}

export function clipImageUrl(id: number): string {
  const t = session ? '?t=' + encodeURIComponent(session.token) : '';
  return `${BASE}/api/crops/${id}/image${t}`;
}

export function publicClipImageUrl(token: string): string {
  return `${BASE}/api/public/c/${token}/image`;
}

export function publicAdImageUrl(token: string, ad: { id: number; version: number }): string {
  return `${BASE}/api/public/s/${token}/promo/${ad.id}/image?v=${ad.version}`;
}

export function adImageUrl(ad: { id: number; version: number }): string {
  const t = session ? '&t=' + encodeURIComponent(session.token) : '';
  return `${BASE}/api/promo/${ad.id}/image?v=${ad.version}${t}`;
}
